import React, { useState, useEffect } from "react";
import AxiosInstance from "../../services/networkServices";
import ReactDOMServer from "react-dom/server";
import { Button } from "@mui/material";
import { Add } from "@mui/icons-material";
import PageLayout from "../../components/PageLayout";
import { ToastContainer, toast } from "react-toastify";
import useDebounce from "../ManageUsers/useDebounce";
import { useDispatch } from "react-redux";
import { setLoading, showToast } from "../../store/slices/common";
import { usePermissions } from "../../hooks/usePermissions";
import { iconMap } from "./categoryIcons";
import { useStyles } from "./styles";
import CategoryTable from "./CategoryTable";
import CategoryFormDialog from "./CategoryFormDialog";
import ViewCategoryDialog from "./ViewCategoryDialog";
import EditCategoryDialog from "./EditCategoryDialog";
import IconPickerDialog from "./IconPickerDialog";
import DeleteCategoryDialog from "./DeleteCategoryDialog";

const CategoryManager = () => {
  const classes = useStyles();
  const [categories, setCategories] = useState([]);
  const [categoryName, setCategoryName] = useState("");
  const [categoryHashtag, setCategoryHashtag] = useState("");
  const [hashtags, setHashtags] = useState([]);
  const [selectedIcon, setSelectedIcon] = useState("");
  const [customIcon, setCustomIcon] = useState(null);
  const [isEditMode, setIsEditMode] = useState(false);
  const [editIndex, setEditIndex] = useState(null);
  const [openDialog, setOpenDialog] = useState(false);
  const [openIconDialog, setOpenIconDialog] = useState(false);
  const searchTerm = "";
  const [page, setPage] = useState(0);
  // const [loading, setLoading] = useState(false);
  const [categoryType, setCategoryType] = useState("");
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [deleteIndex, setDeleteIndex] = useState(null);
  const [openEditDialog, setOpenEditDialog] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  const [anchorElFilter, setAnchorElFilter] = useState(null);
  const [selectedCategoryIndex, setSelectedCategoryIndex] = useState(null);
  const [openViewDialog, setOpenViewDialog] = useState(false);
  const [postFilter, setPostFilter] = useState("");
  const [search, setSearch] = useState("");
  const [totalCategories, setTotalcategories] = useState(0);
  const [toastId, setToastId] = useState(null);
  const debouncedSearch = useDebounce(search, 500);
  const dispatch = useDispatch();
  const [categoryHashtags, setCategoryHashtags] = useState("");
  const [categoryLogo, setCategoryLogo] = useState("");
  const rowsPerPage = 15;
  const { isAdmin } = usePermissions();

  const handleSearchChange = (event) => {
    setSearch(event.target.value);
    setPage(0);
  };

  const handleClick = (event, index) => {
    setAnchorEl(event.currentTarget);
    setSelectedCategoryIndex(index);
  };
  const handleClickFilter = (event, index) => {
    setAnchorElFilter(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
    setSelectedCategoryIndex(null);
  };
  const handleCloseFilter = () => {
    setAnchorElFilter(null);
  };

  const handleOpenDeleteDialog = (index) => {
    setDeleteIndex(index);
    setOpenDeleteDialog(true);
  };
  const validateCategory = () => {
    if (!categoryType) {
      dispatch(
        showToast({ color: "error", msg: "Category Type is required." })
      );
      return false;
    }
    if (!categoryName.trim()) {
      dispatch(
        showToast({ color: "error", msg: "Category Name is required." })
      );
      return false;
    }
    if (!selectedIcon && !customIcon?.url) {
      dispatch(
        showToast({ color: "error", msg: "Icon selection is required." })
      );
      return false;
    }
    // if (hashtags.length === 0) {
    //   dispatch(
    //     showToast({ color: "error", msg: "At least one hashtag is required." })
    //   );
    //   return false;
    // }
    return true;
  };

  const handleCloseDeleteDialog = () => {
    setDeleteIndex(null);
    setOpenDeleteDialog(false);
  };

  const handleConfirmDelete = async () => {
    try {
      const categoryId = categories[deleteIndex]._id;
      await AxiosInstance.delete(`/category/${categoryId}`);
      setCategories(categories.filter((_, i) => i !== deleteIndex));
      // dispatch(showToast({color:"success",msg:"Category deleted successfully"}))
    } catch (error) {
      console.error("Error deleting category:", error);
    } finally {
      handleCloseDeleteDialog();
    }
  };

  useEffect(() => {
    fetchCategories();
  }, [page, rowsPerPage, postFilter, debouncedSearch]);

  const fetchCategories = async () => {
    let payload = { page: page + 1, limit: rowsPerPage };
    if (debouncedSearch) {
      payload = { ...payload, search: debouncedSearch };
    }
    if (postFilter) {
      payload = { ...payload, type: postFilter.toLowerCase() };
    }
    dispatch(setLoading(true));
    try {
      const response = await AxiosInstance.post("/category/list", payload);
      if (response.data.status) {
        const mappedCategories = response.data.data.list.map((category) => ({
          ...category,
          logo: category.logo || "",
          hashtags: category.hashtags || [],
          type: category.type || postFilter.toLowerCase(),
        }));
        setTotalcategories(response?.data?.data?.resultCount);
        setCategories(mappedCategories);
      } else {
        console.error("Failed to fetch categories:", response.data.message);
      }
    } catch (error) {
      console.error("Error fetching categories:", error);
    } finally {
      dispatch(setLoading(false));
    }
  };

  const handleOpenDialog = () => {
    setOpenDialog(true);
    setSelectedIcon("");
    setCustomIcon(null);
    setCategoryName("");
    setHashtags([]);
    setCategoryHashtag("");
    setIsEditMode(false);
    setEditIndex(null);
  };

  const handleCloseDialog = () => {
    setCategoryName("");
    setCategoryHashtag("");
    setSelectedIcon("");
    setCustomIcon(null);
    setIsEditMode(false);
    setEditIndex(null);
    setHashtags([]);
    setOpenDialog(false);
  };
  const handleViewCategory = (index) => {
    console.log("Viewing category at index:", index);
    setSelectedCategoryIndex(index);
    setOpenViewDialog(true);
  };

  const handleCloseViewDialog = () => {
    setOpenViewDialog(false);
  };

  const handleOpenIconDialog = () => {
    setOpenIconDialog(true);
  };

  const handleCloseIconDialog = () => {
    setOpenIconDialog(false);
  };

  const handleAddCategory = async () => {
    if (!validateCategory()) {
      return;
    }
    try {
      dispatch(setLoading(true));
      const logoId = !customIcon ? await uploadIcon(selectedIcon) : "";
      const payload = {
        name: categoryName,
        logoId: customIcon ? customIcon?._id : logoId,
        hashtags: hashtags || [],
        type: categoryType.toLowerCase(),
      };
      await AxiosInstance.post("/category/", payload);
      page !== 0 ? setPage(0) : fetchCategories();
      handleCloseDialog();
    } catch (error) {
      console.error("Error adding category:", error);
    } finally {
      dispatch(setLoading(false));
    }
  };

  const handleCustomUpload = async (file) => {
    try {
      dispatch(dispatch(setLoading(true)));
      const formData = new FormData();
      formData.append("file", file?.file, file?.file?.name);
      formData.append("folder", "icons");
      setOpenIconDialog(false);
      const response = await AxiosInstance.post("/upload/", formData, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
        transformRequest: [
          (data, headers) => {
            delete headers["Content-Type"];
            return data;
          },
        ],
      });
      setCustomIcon(response?.data?.data?.data);
    } catch (err) {
      toast.error("Failed to upload");
    } finally {
      dispatch(setLoading(false));
    }
  };
  const uploadIcon = async (iconName) => {
    try {
      const formData = new FormData();
      const IconComponent = iconMap[iconName];
      if (!IconComponent) {
        throw new Error(`Icon component ${iconName} not found`);
      }
      let svgString = ReactDOMServer.renderToStaticMarkup(
        React.createElement(IconComponent)
      );

      if (
        iconName === "Dance" ||
        iconName === "Fashion" ||
        iconName === "Makeup" ||
        iconName === "Karaoke" ||
        iconName === "Chat" ||
        iconName === "Food" ||
        iconName === "Humour"
      ) {
        svgString = svgString.replace(/fill="[^"]*"/g, 'fill="currentColor"');
      }
      const blob = new Blob([svgString], { type: "image/svg+xml" });

      formData.append("file", blob, `${iconName}.svg`);
      formData.append("folder", "icons");
      // }

      const response = await AxiosInstance.post("/upload/", formData, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
        transformRequest: [
          (data, headers) => {
            delete headers["Content-Type"];
            return data;
          },
        ],
      });
      setCustomIcon(null);
      return response.data.data.data._id;
    } catch (error) {
      console.error(
        "Error uploading icon:",
        error.response ? error.response.data : error.message
      );
      throw error;
    }
  };

  const handleEditCategory = (index) => {
    console.log("Editing category:", categories[index]);
    setCategoryName(categories[index].name);
    setHashtags(categories[index].hashtags);
    setSelectedIcon(categories[index].logo);
    setCategoryType(categories[index].type === "post" ? "Post" : "Live");
    setIsEditMode(true);
    setEditIndex(index);
    setOpenDialog(true);
  };

  const handleUpdateCategory = async () => {
    if (!validateCategory()) {
      return;
    }
    try {
      const logoId =
        !customIcon && selectedIcon && selectedIcon?.substring(0, 4) !== "http"
          ? await uploadIcon(selectedIcon)
          : customIcon?._id;
      let updatedCategory = {
        name: categoryName,
        hashtags: hashtags || [],
      };
      if (logoId) updatedCategory = { ...updatedCategory, logoId: logoId };
      const categoryId = categories[editIndex]._id;
      await AxiosInstance.put(`/category/${categoryId}`, updatedCategory);
      page !== 0 ? setPage(0) : fetchCategories();
      handleCloseDialog();
    } catch (error) {
      console.error("Error updating category:", error);
    }
  };

  const handleSaveEdit = async () => {
    try {
      const categoryId = categories[editIndex]._id;
      await AxiosInstance.put(`/category/${categoryId}`, {
        name: categoryName,
        hashtags: categoryHashtags.split(",").map((tag) => tag.trim()),
        logo: categoryLogo,
      });

      const updatedCategories = [...categories];
      updatedCategories[editIndex] = {
        ...updatedCategories[editIndex],
        name: categoryName,
        hashtags: categoryHashtags.split(",").map((tag) => tag.trim()),
        logo: categoryLogo,
      };
      setCategories(updatedCategories);
      toast.success("Category updated successfully!");
    } catch (error) {
      console.error("Error updating category:", error);
    } finally {
      handleCloseEditDialog();
    }
  };

  const handleCloseEditDialog = () => {
    setOpenEditDialog(false);
    setIsEditMode(false);
    setEditIndex(null);
  };

  const handleIconSelect = (iconName) => {
    setSelectedIcon(selectedIcon === iconName ? "" : iconName);
  };

  const handleSaveIcon = () => {
    handleCloseIconDialog();
  };

  const handleToast = (msg) => {
    if (toastId) {
      toast.dismiss(toastId);
      setToastId(null);
    }
    const id = toast.error(msg);
    setToastId(id);
  };

  const handleHashtagInputKeyDown = (e) => {
    if (
      e.key === "Enter" &&
      categoryHashtag.trim() !== "" &&
      hashtags?.length < 5
    ) {
      setHashtags([...hashtags, categoryHashtag.trim()]);
      setCategoryHashtag("");
    } else if (hashtags.length === 5 && e.key === "Enter") {
      handleToast("Cannot add more than 5 hashtags");
    }
  };
  const handleCategoryTypeChange = (type) => {
    setCategoryType(type);
  };

  const handleRemoveHashtag = (hashtagToRemove) => {
    const updatedHashtags = hashtags.filter(
      (hashtag) => hashtag !== hashtagToRemove
    );
    setHashtags(updatedHashtags);
  };

  const filteredCategories = categories.filter((category) =>
    category.name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const handlePageChange = (event, newPage) => {
    setPage(newPage);
  };

  const handleCategoryChange = (e) => {
    const inputValue = e.target.value;
    const sanitizedValue = inputValue.replace(/[^a-zA-Z0-9\s]/g, "");
    setCategoryName(sanitizedValue);
  };

  return (
    <PageLayout
      title="Categories"
      actions={
        <Button
          variant="contained"
          sx={{
            padding: {
              xs: "6px",
              sm: "6px 16px",
              md: "6px 16px",
              lg: "6px 16px",
              xl: "6px 16px",
            },
            backgroundColor: "#142C5C",
            color: "#fff",
            borderColor: "#0f2247",
            "&:hover": {
              backgroundColor: "#0f2247",
            },
            borderRadius: "6px",
            fontSize: {
              xs: "12px",
              sm: "13px",
              md: "13px",
              lg: "14px",
              xl: "14px",
            },
            height: {
              xs: "35px",
              sm: "40px",
              md: "40px",
              lg: "40px",
              xl: "40px",
            },
          }}
          onClick={handleOpenDialog}
        >
          <Add /> Add Category
        </Button>
      }
    >
      <ToastContainer />
      <CategoryTable classes={classes} setPostFilter={setPostFilter} handleSearchChange={handleSearchChange} filteredCategories={filteredCategories} anchorElFilter={anchorElFilter} handleClickFilter={handleClickFilter} handleCloseFilter={handleCloseFilter} anchorEl={anchorEl} selectedCategoryIndex={selectedCategoryIndex} handleClick={handleClick} handleClose={handleClose} handleEditCategory={handleEditCategory} handleOpenDeleteDialog={handleOpenDeleteDialog} handleViewCategory={handleViewCategory} isAdmin={isAdmin} rowsPerPage={rowsPerPage} totalCategories={totalCategories} page={page} handlePageChange={handlePageChange} />

      <CategoryFormDialog openDialog={openDialog} handleCloseDialog={handleCloseDialog} isEditMode={isEditMode} categoryType={categoryType} handleCategoryTypeChange={handleCategoryTypeChange} categoryName={categoryName} handleCategoryChange={handleCategoryChange} handleOpenIconDialog={handleOpenIconDialog} customIcon={customIcon} selectedIcon={selectedIcon} categoryHashtag={categoryHashtag} setCategoryHashtag={setCategoryHashtag} handleHashtagInputKeyDown={handleHashtagInputKeyDown} hashtags={hashtags} handleRemoveHashtag={handleRemoveHashtag} handleUpdateCategory={handleUpdateCategory} handleAddCategory={handleAddCategory} />
      <ViewCategoryDialog openViewDialog={openViewDialog} handleCloseViewDialog={handleCloseViewDialog} selectedCategoryIndex={selectedCategoryIndex} categories={categories} />
      <EditCategoryDialog openEditDialog={openEditDialog} handleCloseEditDialog={handleCloseEditDialog} categoryName={categoryName} setCategoryName={setCategoryName} categoryHashtags={categoryHashtags} setCategoryHashtags={setCategoryHashtags} categoryLogo={categoryLogo} setCategoryLogo={setCategoryLogo} handleSaveEdit={handleSaveEdit} />

      <IconPickerDialog openIconDialog={openIconDialog} handleCloseIconDialog={handleCloseIconDialog} handleCustomUpload={handleCustomUpload} selectedIcon={selectedIcon} handleIconSelect={handleIconSelect} handleSaveIcon={handleSaveIcon} />

      <DeleteCategoryDialog openDeleteDialog={openDeleteDialog} handleCloseDeleteDialog={handleCloseDeleteDialog} handleConfirmDelete={handleConfirmDelete} />
    </PageLayout>
  );
};

export default CategoryManager;
